import React, { useState } from 'react';
import { auth, googleProvider, facebookProvider, signInWithEmailAndPassword, signInWithPopup } from './firebase';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FaEnvelope, FaLock, FaGoogle, FaFacebook, FaGamepad } from 'react-icons/fa';
import { addDocument, getDocument } from './firestore';

const ensureGameplayData = async (user) => {
  const gameplayDoc = await getDocument('gameplay', user.uid);
  if (!gameplayDoc?.exists()) {
    let firstName = '';
    let lastName = '';
    if (user.displayName) {
      const nameParts = user.displayName.trim().split(' ');
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }
    await addDocument('users', user.uid, {
        email: user.email,
        games: 0,
        score: 0,
        streak: 1,
        streak_updated: user.metadata.creationTime,
        question_answered: 0,
        question_correct: 0,
        firstName: firstName,
        lastName: lastName,
        created_at: user.metadata.creationTime
      });
    console.log(user);
      // for storing the gameplay data total
    await addDocument('gameplay', user.uid, {
      firstName: user.displayName?.split(' ')[0] || 'First',
      lastName: user.displayName?.split(' ')[1] || 'Last',
      total_questions: 0,
      questions_answered: 0,
      score: 0,
      games_played: 0
    });
    console.log('Gameplay data created for logged-in user');
  }
};

const Login = ({ isLightMode }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = location.state?.from?.pathname || '/';

    // Handle email/password login
    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            console.log('User logged in:', userCredential.user);
            navigate(redirectTo);
        } catch (error) {
            setError(
                error.code === 'auth/invalid-credential' 
                    ? 'Invalid email or password. Please try again.' 
                    : error.message
            );
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Google login
    const handleGoogleLogin = async () => {
  setIsLoading(true);
  setError(null);
  try {
    const result = await signInWithPopup(auth, googleProvider);
    await ensureGameplayData(result.user); // 💡 Add this line
    console.log('User logged in with Google:', result.user);
    navigate(redirectTo);
  } catch (error) {
    setError(error.message);
  } finally {
    setIsLoading(false);
  }
};

const handleFacebookLogin = async () => {
  setIsLoading(true);
  setError(null);
  try {
    const result = await signInWithPopup(auth, facebookProvider);
    await ensureGameplayData(result.user); // 💡 And this too
    console.log('User logged in with Facebook:', result.user);
    navigate(redirectTo);
  } catch (error) {
    setError(error.message);
  } finally {
    setIsLoading(false);
  }
};

    return (
        <div className="min-h-screen bg-[var(--background-color)] flex justify-center items-center p-4 relative overflow-hidden">
            {/* Background effects */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-[var(--primary-color)] opacity-10 rounded-full blur-3xl transform translate-x-1/4 -translate-y-1/4"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-[var(--accent-color)] opacity-10 rounded-full blur-3xl transform -translate-x-1/4 translate-y-1/4"></div>
                <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-[var(--interactive-color)] opacity-10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
            </div>
            
            <div className="container max-w-6xl mx-auto z-10">
                <div className="flex flex-col md:flex-row rounded-2xl shadow-2xl overflow-hidden">
                    {/* Left side - Branding */}
                    <div className="w-full md:w-2/5 bg-gradient-to-br from-[var(--primary-color)] to-[#0D1821] text-white p-8 flex flex-col relative overflow-hidden">
                        {/* Decorative elements */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full transform translate-x-1/3 -translate-y-1/3"></div>
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full transform -translate-x-1/3 translate-y-1/3"></div>
                        
                        {/* Content */}
                        <div className="z-10 flex flex-col h-full justify-between">
                            <div className="mb-8">
                                <h1 className="text-4xl md:text-5xl font-bold mb-4 text-[var(--accent-color)]">QUIZ ARCADE</h1>
                                <p className="text-xl md:text-2xl font-medium opacity-90">QUIZ WHILE YOU PLAY – ARE YOU READY FOR THE ARCADE?</p>
                            </div>
                            
                            <div className="flex-grow flex items-center justify-center">
                                <div className="relative w-full max-w-md">
                                    <FaGamepad className="absolute text-[var(--accent-color)] text-8xl opacity-20 transform rotate-12" />
                                    <img 
                                        src="/controller.png" 
                                        alt="Gaming Controller" 
                                        className="w-full max-w-sm mx-auto transform transition-transform hover:scale-105 duration-300 relative z-10"
                                    />
                                </div>
                            </div>
                            
                            <div className="mt-8">
                                <p className="text-sm opacity-80">Enter the world of knowledge and gaming combined in one ultimate experience.</p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Right side - Login Form */}
                    <div className="w-full md:w-3/5 bg-[var(--surface-color)] p-8 md:p-12">
                        <div className="max-w-md mx-auto">
                            <h2 className="text-3xl font-bold mb-6 text-[var(--text-color)]">Welcome Back!</h2>
                            
                            {error && (
                                <div className="mb-6 p-3 bg-[var(--error-color)] bg-opacity-10 border border-[var(--error-color)] rounded-lg">
                                    <p className="text-[var(--error-color)]">{error}</p>
                                </div>
                            )}
                            
                            <form onSubmit={handleEmailLogin} className="space-y-6">
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-[var(--text-color)] mb-1">
                                        Email Address
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <FaEnvelope className="text-[var(--secondary-color)]" />
                                        </div>
                                        <input 
                                            id="email"
                                            type="email" 
                                            name="email" 
                                            placeholder="Enter your email" 
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            className="w-full pl-10 p-3 bg-[var(--background-color)] text-[var(--text-color)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--interactive-color)] transition-colors"
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium text-[var(--text-color)] mb-1">
                                        Password
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <FaLock className="text-[var(--secondary-color)]" />
                                        </div>
                                        <input 
                                            id="password"
                                            type="password" 
                                            name="password" 
                                            placeholder="Enter your password" 
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className="w-full pl-10 p-3 bg-[var(--background-color)] text-[var(--text-color)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--interactive-color)] transition-colors"
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center">
                                        <input 
                                            id="remember"
                                            type="checkbox" 
                                            className="w-4 h-4 text-[var(--interactive-color)] bg-[var(--background-color)] border-[var(--border-color)] rounded focus:ring-[var(--interactive-color)]" 
                                        />
                                        <label htmlFor="remember" className="ml-2 text-sm text-[var(--text-color)]">
                                            Remember me
                                        </label>
                                    </div>
                                    <a href="#" className="text-sm text-[var(--interactive-color)] hover:underline">
                                        Forgot password?
                                    </a>
                                </div>
                                
                                <button 
                                    type="submit" 
                                    disabled={isLoading}
                                    className="w-full bg-[var(--interactive-color)] hover:bg-opacity-90 text-white font-medium py-3 px-4 rounded-lg transition duration-300 flex justify-center items-center"
                                >
                                    {isLoading ? (
                                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : 'Login'}
                                </button>
                                
                                <div className="text-center">
                                    <p className="text-[var(--text-color)]">
                                        Don't have an account? <Link to="/registration" className="text-[var(--interactive-color)] hover:underline">Sign Up</Link>
                                    </p>
                                </div>
                                
                                <div className="relative my-6">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-[var(--border-color)]"></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-[var(--surface-color)] text-[var(--secondary-color)]">Or continue with</span>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <button
                                        type="button"
                                        onClick={handleGoogleLogin}
                                        disabled={isLoading}
                                        className="bg-white text-gray-700 hover:bg-gray-100 font-medium p-3 rounded-lg border border-gray-300 flex items-center justify-center transition duration-300"
                                    >
                                        <FaGoogle className="mr-2 text-red-500" /> Login with Google
                                    </button>
                                    
                                    <button
                                        type="button"
                                        onClick={handleFacebookLogin}
                                        disabled={isLoading}
                                        className="bg-white text-gray-700 hover:bg-gray-100 font-medium p-3 rounded-lg border border-gray-300 flex items-center justify-center transition duration-300"
                                    >
                                        <FaFacebook className="mr-2 text-blue-600" /> Login with Facebook
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;